/**
 * useAnalytics — Batched event capture hook for platform usage tracking.
 *
 * Usage:
 *   // In a top-level component (AppLayout) to start the flush timer and sync org/user IDs:
 *   useAnalytics();
 *
 *   // From anywhere (inside or outside React) to capture events:
 *   import { trackEvent } from '@/hooks/useAnalytics';
 *   trackEvent('report_export', { report_type: 'trips', format: 'csv' });
 *
 * Event types (D-02):
 *   - 'page_view'       { page: '/map' }
 *   - 'login'           {}  (no PII — do not include email)
 *   - 'feature_use'     { feature: 'report_export', action: 'csv' }
 *   - 'report_export'   { report_type: 'trips', format: 'csv' }
 *   - 'rule_create'     { tier: 1 }
 *   - 'dispatch_create' {}
 *   - 'device_command'  {}
 *
 * Rules:
 *   - Events without org_id are silently dropped (D-03).
 *   - No PII in event_data — caller's responsibility (D-05).
 *   - Failed flush batches are dropped; no retry (D-06).
 *   - No user-visible errors — console.warn only (D-06).
 */

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getAuthToken } from '@/lib/trackizy';

// ─── Module-level singletons ───────────────────────────────────────────────
// These live outside React so trackEvent() can be called from anywhere.

/** Pending events waiting to be flushed. */
let _buffer = [];

/** Interval ID for the 30-second flush timer. */
let _flushTimer = null;

/** Cached org_id from Redux — updated each render of useAnalytics. */
let _orgId = null;

/** Cached user_id from Redux — updated each render of useAnalytics. */
let _userId = null;

/** Whether a beforeunload listener has been registered (once per page load). */
let _unloadRegistered = false;

// ─── Internal flush ────────────────────────────────────────────────────────

/**
 * Drains the buffer and posts the batch to the server.
 * Silent on failure — never throws.
 */
async function _flush() {
  if (_buffer.length === 0) return;

  // Atomically splice the buffer so new events can be pushed while we send.
  const batch = _buffer.splice(0, _buffer.length);

  try {
    const token = getAuthToken();
    const res = await fetch('/api/analytics/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(batch),
    });
    if (!res.ok) {
      console.warn('[analytics] flush error:', res.statusText);
    }
  } catch (err) {
    console.warn('[analytics] flush exception:', err?.message);
  }
}

/**
 * Flush using fetch + keepalive so the request survives page unload.
 */
function _flushOnUnload() {
  if (_buffer.length === 0) return;

  const batch = _buffer.splice(0, _buffer.length);
  const token = getAuthToken();

  try {
    fetch('/api/analytics/events', {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(batch),
    });
    // Do not await — fire and forget. Page is unloading.
  } catch {
    // Swallow — page is closing
  }
}

// ─── Public trackEvent ─────────────────────────────────────────────────────

/**
 * Buffer an analytics event for batch insertion.
 *
 * @param {string} eventType  - One of the documented event types above.
 * @param {Object} [eventData={}]  - Arbitrary metadata. Must NOT contain PII.
 */
export function trackEvent(eventType, eventData = {}) {
  if (!_orgId) return; // Events without org_id are silently dropped (D-03)

  _buffer.push({
    org_id: _orgId,
    user_id: _userId,
    event_type: eventType,
    event_data: eventData,
    created_at: new Date().toISOString(),
  });
}

// ─── useAnalytics hook ─────────────────────────────────────────────────────

/**
 * Mount in AppLayout (once) to:
 *   1. Keep module-level _orgId / _userId in sync with Redux.
 *   2. Start the 30-second flush interval.
 *   3. Register a beforeunload listener for final flush on page close.
 *
 * Returns `{ trackEvent }` for convenience.
 */
export default function useAnalytics() {
  // The enterprise view's synthetic org (`enterprise:<id>`) is not an org:
  // /api/analytics/events is org-scoped, so enterprise mode records nothing.
  const orgId = useSelector((state) => (state.org?.enterprise === true ? null : state.org?.id ?? null));
  const userId = useSelector((state) => state.session?.user?.id ?? null);

  // Keep module-level cache in sync with Redux so trackEvent() always has
  // the latest org_id even when called outside React render.
  useEffect(() => {
    _orgId = orgId;
    _userId = userId;
  }, [orgId, userId]);

  // Start the 30-second flush interval on mount (D-01).
  useEffect(() => {
    // Clear any stale timer from hot module reload in dev.
    if (_flushTimer) {
      clearInterval(_flushTimer);
    }
    _flushTimer = setInterval(_flush, 30_000);

    // Register beforeunload once per page session.
    if (!_unloadRegistered) {
      _unloadRegistered = true;
      window.addEventListener('beforeunload', _flushOnUnload);
    }

    return () => {
      // Flush on unmount (e.g. when AppLayout unmounts on logout).
      _flush();
      clearInterval(_flushTimer);
      _flushTimer = null;
      // Do NOT remove the beforeunload listener — it needs to survive until the tab closes.
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { trackEvent };
}
